import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { GoogleButton } from "@/components/site/GoogleAuth";

const Shell = ({ title, sub, children }: { title: string; sub: string; children: React.ReactNode }) => (
  <div className="container-x flex justify-center py-16 sm:py-24">
    <div className="w-full max-w-md fade-up">
      <h1 className="text-4xl tracking-tight" data-testid="auth-title">{title}</h1>
      <p className="mt-3 text-sm text-navy/60">{sub}</p>
      <div className="card-lux mt-8 p-6 sm:p-8">{children}</div>
    </div>
  </div>
);

export const Login = () => {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [mfa, setMfa] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/auth/login", { email, password, mfa_code: mfa ? code : undefined });
      if (r.data.mfa_required) { setMfa(true); return; }
      setUser(r.data);
      const staff = ["owner", "admin", "staff"].includes(r.data.role);
      nav(params.get("next") || (staff ? "/admin" : "/account"));
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Shell title="Welcome back" sub="Sign in to view your booking history or manage the business.">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <div><label className="label">Email</label><input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="field" data-testid="login-email" /></div>
        <div><label className="label">Password</label><input required type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="field" data-testid="login-password" /></div>
        {mfa && <div><label className="label">Authenticator code</label><input required inputMode="numeric" value={code} onChange={(e) => setCode(e.target.value)} className="field font-mono tracking-[0.4em]" data-testid="login-mfa-code" /></div>}
        <button disabled={busy} className="btn-navy w-full" data-testid="login-submit">{busy && <Loader2 size={16} className="animate-spin" />} {mfa ? "Verify & sign in" : "Sign in"}</button>
        <div className="flex justify-between text-sm"><Link to="/forgot-password" className="underline" data-testid="login-forgot-link">Forgot password?</Link><Link to="/register" className="underline" data-testid="login-register-link">Create account</Link></div>
        <div className="flex items-center gap-3 text-xs text-navy/40"><span className="h-px flex-1 bg-navy/10" />or<span className="h-px flex-1 bg-navy/10" /></div>
        <GoogleButton next={params.get("next") || "/account"} />
      </form>
    </Shell>
  );
};

export const Register = () => {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ name: "", email: "", phone: "", password: "" });
  const [busy, setBusy] = useState(false);
  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement>) => setF({ ...f, [k]: e.target.value });
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/auth/register", { ...f, phone: f.phone || undefined });
      setUser(r.data);
      nav("/account");
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Shell title="Create your account" sub="Keep a history of requests you make while signed in. You can always look up bookings by reference too.">
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <input required placeholder="Full name" value={f.name} onChange={set("name")} className="field" data-testid="register-name" />
        <input required type="email" placeholder="Email" value={f.email} onChange={set("email")} className="field" data-testid="register-email" />
        <input placeholder="Phone (optional)" value={f.phone} onChange={set("phone")} className="field" data-testid="register-phone" />
        <input required type="password" minLength={8} placeholder="Password (8+ characters)" value={f.password} onChange={set("password")} className="field" data-testid="register-password" />
        <button disabled={busy} className="btn-navy w-full" data-testid="register-submit">{busy && <Loader2 size={16} className="animate-spin" />} Create account</button>
        <p className="text-center text-sm">Already have an account? <Link to="/login" className="underline">Sign in</Link></p>
      </form>
    </Shell>
  );
};

export const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState("");
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    try { setMsg((await api.post("/auth/forgot-password", { email })).data.message); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Shell title="Reset password" sub="We will email you a secure reset link.">
      {msg ? <p className="text-sm" data-testid="forgot-success">{msg}</p> : (
        <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
          <input required type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className="field" data-testid="forgot-email" />
          <button className="btn-navy w-full" data-testid="forgot-submit">Send reset link</button>
        </form>
      )}
    </Shell>
  );
};

export const ResetPassword = () => {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post("/auth/reset-password", { token: params.get("token"), password });
      toast.success("Password updated. Please sign in.");
      nav("/login");
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Shell title="Choose a new password" sub="Use at least 8 characters.">
      <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
        <input required type="password" minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} className="field" data-testid="reset-password" />
        <button className="btn-navy w-full" data-testid="reset-submit">Update password</button>
      </form>
    </Shell>
  );
};
